import { useEffect, useState, useRef, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Ticket, Calendar, Heart, User as UserIcon, ClipboardCheck, MessageCircle, Crown, Send, Sparkles, Upload } from "lucide-react";
import { parseEventDate, formatEventDate } from "@/lib/utils";

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [matches, setMatches] = useState([]);
  const [threads, setThreads] = useState([]);
  const [activeThread, setActiveThread] = useState(null); // { other, messages }
  const [msgDraft, setMsgDraft] = useState("");
  const [subscription, setSubscription] = useState({ status: "none" });
  const [plans, setPlans] = useState({});
  const [profile, setProfile] = useState({});
  const [attendeeData, setAttendeeData] = useState({}); // event_id -> { attendees, is_past_event, event }
  const [selections, setSelections] = useState({});
  const [vipBusy, setVipBusy] = useState(null);
  const [uploadBusy, setUploadBusy] = useState(false);
  const fileInputRef = useRef();
  const [params] = useSearchParams();
  const bottomRef = useRef();

  const reload = useCallback(() => {
    const warn = (label) => (err) => console.warn(`Dashboard ${label} fetch failed`, err);
    api.get("/tickets/mine").then(r => setTickets(r.data)).catch(warn("tickets"));
    api.get("/matches/mine").then(r => setMatches(r.data)).catch(warn("matches"));
    api.get("/messages/threads").then(r => setThreads(r.data)).catch(warn("threads"));
    api.get("/subscriptions/mine").then(r => setSubscription(r.data)).catch(warn("subscription"));
    api.get("/subscriptions/plans").then(r => setPlans(r.data)).catch(warn("plans"));
  }, []);

  useEffect(() => {
    reload();
    setProfile({
      name: user?.name || "", age: user?.age || "", city: user?.city || "",
      occupation: user?.occupation || "", about: user?.about || "",
      interests: (user?.interests || []).join(", "), relationship_goals: user?.relationship_goals || "",
      pets: user?.pets || "", children: user?.children || "", favorite_date_idea: user?.favorite_date_idea || "",
      photo_url: user?.photo_url || "",
    });
  }, [user, reload]);

  // Handle Stripe subscription return
  useEffect(() => {
    const sid = params.get("session_id");
    if (params.get("vip") === "success") {
      toast.success("Payment received — activating your VIP membership...");
      const verify = async () => {
        try {
          if (sid) await api.get(`/subscriptions/verify/${sid}`);
        } catch (err) {
          console.warn("VIP subscription verify failed (webhook may still confirm)", err);
        }
        await refresh();
        api.get("/subscriptions/mine").then(r => setSubscription(r.data)).catch(err => console.warn("VIP status refresh failed", err));
      };
      verify();
    }
    if (params.get("vip") === "cancel") toast.info("VIP checkout cancelled.");
  }, [params, refresh]);

  const saveProfile = async () => {
    const payload = {
      ...profile,
      interests: profile.interests.split(",").map(s=>s.trim()).filter(Boolean),
      age: profile.age ? +profile.age : undefined,
    };
    try { await api.patch("/profile", payload); await refresh(); toast.success("Profile saved"); }
    catch { toast.error("Save failed"); }
  };

  const loadAttendees = async (eventId) => {
    try {
      const { data } = await api.get(`/events/${eventId}/attendees`);
      setAttendeeData(a => ({ ...a, [eventId]: data }));
    } catch (e) { toast.error(e.response?.data?.detail || "Could not load attendees"); }
  };

  const toggleSel = (eventId, uid) => {
    setSelections(prev => {
      const s = new Set(prev[eventId] || []);
      s.has(uid) ? s.delete(uid) : s.add(uid);
      return { ...prev, [eventId]: s };
    });
  };

  const submitSelections = async (eventId) => {
    const arr = Array.from(selections[eventId] || []);
    try {
      await api.post("/matches/select", { event_id: eventId, selected_user_ids: arr });
      toast.success("Selections saved. If it's mutual, both of you will be notified.");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save selections"); }
  };

  const openThread = async (otherUserId) => {
    try {
      const { data } = await api.get(`/messages/thread/${otherUserId}`);
      setActiveThread(data);
      reload();
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch { toast.error("Could not open thread"); }
  };

  const sendMessage = async () => {
    if (!msgDraft.trim() || !activeThread?.other) return;
    try {
      await api.post("/messages/send", { to_user_id: activeThread.other.user_id, body: msgDraft.trim() });
      setMsgDraft("");
      openThread(activeThread.other.user_id);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send"); }
  };

  const startCheckout = async (plan) => {
    setVipBusy(plan);
    try {
      const { data } = await api.post("/subscriptions/checkout", { plan, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      setVipBusy(null);
      toast.error(e.response?.data?.detail || "Checkout failed");
    }
  };

  const cancelVip = async () => {
    if (!window.confirm("Cancel VIP at the end of the current billing period?")) return;
    try { await api.post("/subscriptions/cancel"); toast.success("Cancellation scheduled"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Cancel failed"); }
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Photo must be under 5 MB"); return; }
    setUploadBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/profile/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setProfile(p => ({ ...p, photo_url: data.photo_url }));
      await refresh();
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploadBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const now = new Date(new Date().toDateString());
  const upcoming = tickets.filter(t => t.event && parseEventDate(t.event.date) >= now && t.status === "paid");
  const past = tickets.filter(t => t.event && parseEventDate(t.event.date) < now && t.status === "paid");
  const isVip = subscription?.status === "active" || subscription?.status === "trialing";

  return (
    <div className="max-w-6xl mx-auto px-6 py-12" data-testid="dashboard-page">
      <div className="mb-10 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-eyebrow">Your dashboard</div>
          <h1 className="font-heading text-4xl md:text-5xl mt-2 flex items-center gap-3">
            Hi, {user?.name?.split(" ")[0] || "there"}.
            {isVip && <Badge className="bg-gradient-to-r from-amber-300 to-yellow-500 text-black rounded-full px-3 py-1"><Crown size={12} className="mr-1" /> VIP</Badge>}
          </h1>
        </div>
      </div>

      <Tabs defaultValue="upcoming">
        <TabsList className="mb-6 flex-wrap h-auto bg-blush/40 rounded-full p-1">
          <TabsTrigger value="upcoming" className="rounded-full px-5" data-testid="tab-upcoming"><Calendar size={14} className="mr-2" /> Upcoming</TabsTrigger>
          <TabsTrigger value="matching" className="rounded-full px-5" data-testid="tab-matching"><Heart size={14} className="mr-2" /> Post-Event Matching</TabsTrigger>
          <TabsTrigger value="matches" className="rounded-full px-5" data-testid="tab-matches"><Sparkles size={14} className="mr-2" /> Mutual Matches</TabsTrigger>
          <TabsTrigger value="messages" className="rounded-full px-5" data-testid="tab-messages"><MessageCircle size={14} className="mr-2" /> Messages</TabsTrigger>
          <TabsTrigger value="tickets" className="rounded-full px-5" data-testid="tab-tickets"><Ticket size={14} className="mr-2" /> Tickets</TabsTrigger>
          <TabsTrigger value="profile" className="rounded-full px-5" data-testid="tab-profile"><UserIcon size={14} className="mr-2" /> Profile</TabsTrigger>
          <TabsTrigger value="questionnaire" className="rounded-full px-5" data-testid="tab-questionnaire"><ClipboardCheck size={14} className="mr-2" /> Questionnaire</TabsTrigger>
          <TabsTrigger value="vip" className="rounded-full px-5" data-testid="tab-vip"><Crown size={14} className="mr-2" /> VIP</TabsTrigger>
        </TabsList>

        {/* Upcoming */}
        <TabsContent value="upcoming">
          {upcoming.length === 0 ? <Empty text="No upcoming events yet." cta /> : (
            <div className="grid md:grid-cols-2 gap-6">
              {upcoming.map(t => <TicketCard key={t.ticket_id} t={t} />)}
            </div>
          )}
        </TabsContent>

        {/* Post-Event Matching (only past events) */}
        <TabsContent value="matching">
          {past.length === 0 ? (
            <Empty text="Once you've attended an event, come back here to select who you'd like to connect with. Matches are private and only revealed if mutual." />
          ) : (
            <div className="space-y-8">
              {past.map(t => (
                <div key={t.ticket_id} className="card-elegant p-6" data-testid={`past-event-${t.event_id}`}>
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                      <div className="text-eyebrow">Past event</div>
                      <h3 className="font-heading text-2xl mt-1">{t.event?.title}</h3>
                      <div className="text-neutral-600 text-sm mt-1">{formatEventDate(t.event?.date, { weekday: "short", month: "long", day: "numeric" })} · {t.event?.venue}</div>
                    </div>
                    {!attendeeData[t.event_id] && (
                      <Button onClick={()=>loadAttendees(t.event_id)} className="rounded-full bg-black text-white" data-testid={`load-attendees-${t.event_id}`}>Reveal attendees</Button>
                    )}
                  </div>

                  {attendeeData[t.event_id] && (
                    <div className="mt-6">
                      <div className="text-sm text-neutral-600 mb-4">Tap the profiles you'd love to see again. Both of you must select each other for a match.</div>
                      {attendeeData[t.event_id].attendees.length === 0 ? (
                        <div className="text-neutral-500">No other attendees yet.</div>
                      ) : (
                        <>
                          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
                            {attendeeData[t.event_id].attendees.map(a => {
                              const sel = (selections[t.event_id] || new Set()).has(a.user_id);
                              const breakdown = a.compatibility_breakdown || {};
                              return (
                                <button
                                  key={a.user_id}
                                  onClick={()=>toggleSel(t.event_id, a.user_id)}
                                  className={`text-left p-4 rounded-2xl border transition ${sel ? "bg-black text-white border-black shadow-md" : "bg-white border-neutral-200 hover:border-black"}`}
                                  data-testid={`attendee-${a.user_id}`}
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="font-heading text-lg">{a.name || "Guest"}</div>
                                    {a.compatibility_score != null && (
                                      <span className={`text-xs font-mono px-2 py-0.5 rounded-full ${sel ? "bg-white/20 text-white" : "bg-amber-100 text-amber-800"}`} data-testid={`compat-score-${a.user_id}`}>
                                        {a.compatibility_score}%
                                      </span>
                                    )}
                                  </div>
                                  <div className={`text-xs mt-1 ${sel ? "text-white/70" : "text-neutral-500"}`}>
                                    {a.age ? `${a.age} · ` : ""}{a.city || "—"}
                                  </div>
                                  {a.occupation && (
                                    <div className={`text-xs mt-1 ${sel ? "text-white/60" : "text-neutral-400"}`}>{a.occupation}</div>
                                  )}
                                  {Object.keys(breakdown).length > 0 && (
                                    <div className="mt-3 flex flex-wrap gap-1">
                                      {Object.entries(breakdown).map(([cat, pct]) => (
                                        <span
                                          key={cat}
                                          className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${sel ? "bg-white/15 text-white/90" : "bg-neutral-100 text-neutral-700"}`}
                                          data-testid={`compat-cat-${a.user_id}-${cat}`}
                                        >
                                          {cat} {pct}%
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                          <Button onClick={()=>submitSelections(t.event_id)} className="mt-6 rounded-full bg-black text-white btn-pill" data-testid={`submit-selections-${t.event_id}`}>Save my selections</Button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Mutual Matches */}
        <TabsContent value="matches">
          {matches.length === 0 ? (
            <Empty text="No mutual matches yet — attend an event and select folks you'd love to see again." cta />
          ) : (
            <div className="grid md:grid-cols-2 gap-6">
              {matches.map((m, i) => (
                <div key={`${m.event?.event_id}-${m.user?.user_id}`} className="card-elegant p-6" data-testid={`match-card-${i}`}>
                  <div className="text-eyebrow gold">It's mutual</div>
                  <div className="flex items-center justify-between mt-2">
                    <div className="font-heading text-2xl">{m.user?.name}</div>
                    <Heart size={20} className="text-rose-500 fill-rose-500" />
                  </div>
                  <div className="text-neutral-600 text-sm mt-1">{m.user?.age ? `${m.user.age} · ` : ""}{m.user?.city}</div>
                  <div className="text-neutral-500 text-xs mt-3">From: {m.event?.title}</div>
                  <div className="text-neutral-800 text-sm mt-2">Contact: <a className="underline" href={`mailto:${m.user?.email}`}>{m.user?.email}</a></div>
                  <Button onClick={()=>openThread(m.user.user_id)} className="mt-4 w-full rounded-full bg-black text-white" data-testid={`message-match-${i}`}>
                    <MessageCircle size={14} className="mr-2" /> Send a message
                  </Button>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Messages */}
        <TabsContent value="messages">
          <div className="grid md:grid-cols-3 gap-6">
            <div className="md:col-span-1 space-y-2">
              {threads.length === 0 ? (
                <div className="card-elegant p-6 text-neutral-500 text-sm">No conversations yet. Once you have a mutual match you can start a chat.</div>
              ) : threads.map(th => (
                <button
                  key={th.thread_key}
                  onClick={()=>openThread(th.other.user_id)}
                  className={`w-full text-left card-elegant p-4 hover:border-black transition ${activeThread?.other?.user_id === th.other.user_id ? "border-black" : ""}`}
                  data-testid={`thread-${th.other.user_id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="font-heading text-lg">{th.other.name}</div>
                    {th.unread > 0 && <span className="bg-black text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">{th.unread}</span>}
                  </div>
                  {th.last_message && <div className="text-xs text-neutral-500 mt-1 line-clamp-1">{th.last_message.body}</div>}
                  {th.event && <div className="text-[10px] uppercase tracking-widest text-neutral-400 mt-1">from {th.event.title}</div>}
                </button>
              ))}
            </div>
            <div className="md:col-span-2 card-elegant p-0 min-h-[520px] flex flex-col">
              {!activeThread ? (
                <div className="flex-1 flex items-center justify-center text-neutral-500 text-sm p-8">Select a conversation to start chatting.</div>
              ) : (
                <>
                  <div className="p-4 border-b border-neutral-200 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blush/60 flex items-center justify-center font-heading">{activeThread.other?.name?.[0] || "?"}</div>
                    <div>
                      <div className="font-heading text-lg">{activeThread.other?.name}</div>
                      <div className="text-xs text-neutral-500">{activeThread.other?.city || ""}</div>
                    </div>
                  </div>
                  <div className="flex-1 overflow-y-auto p-4 space-y-3 max-h-[420px]" data-testid="message-list">
                    {activeThread.messages.length === 0 && <div className="text-center text-neutral-400 text-sm mt-8">Say hello — every good story starts with the first word.</div>}
                    {activeThread.messages.map(m => {
                      const mine = m.from_user_id === user?.user_id;
                      return (
                        <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                          <div className={`max-w-[75%] rounded-2xl px-4 py-2 text-sm ${mine ? "bg-black text-white rounded-br-sm" : "bg-blush/60 text-neutral-900 rounded-bl-sm"}`}>
                            {m.body}
                            <div className={`text-[10px] mt-1 ${mine ? "text-white/60" : "text-neutral-500"}`}>{new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={bottomRef} />
                  </div>
                  <div className="p-4 border-t border-neutral-200 flex gap-2">
                    <Input
                      value={msgDraft}
                      onChange={e=>setMsgDraft(e.target.value)}
                      onKeyDown={e => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), sendMessage())}
                      placeholder="Type a message…"
                      className="rounded-full"
                      data-testid="message-input"
                    />
                    <Button onClick={sendMessage} className="rounded-full bg-black text-white" data-testid="send-message-btn"><Send size={14} /></Button>
                  </div>
                </>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Tickets */}
        <TabsContent value="tickets">
          {tickets.length === 0 ? <Empty text="You haven't purchased any tickets yet." cta /> : (
            <div className="grid md:grid-cols-2 gap-6">{tickets.map(t => <TicketCard key={t.ticket_id} t={t} />)}</div>
          )}
        </TabsContent>

        {/* Profile */}
        <TabsContent value="profile">
          <div className="card-elegant p-8">
            <div className="flex flex-col sm:flex-row items-start gap-6 mb-8 pb-8 border-b border-neutral-200">
              <div className="relative">
                <div className="w-28 h-28 rounded-full overflow-hidden bg-blush/60 flex items-center justify-center font-heading text-3xl text-neutral-700 border-2 border-cream shadow-sm" data-testid="profile-photo-preview">
                  {profile.photo_url ? (
                    <img src={profile.photo_url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${profile.photo_url}` : profile.photo_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    profile.name?.[0] || "?"
                  )}
                </div>
              </div>
              <div className="flex-1">
                <div className="text-eyebrow">Profile photo</div>
                <h3 className="font-heading text-2xl mt-1">Show up as your best self.</h3>
                <p className="text-sm text-neutral-600 mt-2">A recent, well-lit photo goes a long way. JPG, PNG, or WEBP, up to 5 MB.</p>
                <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={uploadPhoto} className="hidden" data-testid="profile-photo-input" />
                <Button onClick={()=>fileInputRef.current?.click()} disabled={uploadBusy} className="mt-4 rounded-full bg-black text-white btn-pill" data-testid="upload-photo-btn">
                  <Upload size={14} className="mr-2" /> {uploadBusy ? "Uploading…" : (profile.photo_url ? "Change photo" : "Upload photo")}
                </Button>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <Input placeholder="Name" value={profile.name || ""} onChange={e=>setProfile({...profile, name: e.target.value})} className="rounded-xl" data-testid="profile-name" />
              <Input placeholder="Age" type="number" value={profile.age || ""} onChange={e=>setProfile({...profile, age: e.target.value})} className="rounded-xl" data-testid="profile-age" />
              <Input placeholder="City" value={profile.city || ""} onChange={e=>setProfile({...profile, city: e.target.value})} className="rounded-xl" data-testid="profile-city" />
              <Input placeholder="Occupation" value={profile.occupation || ""} onChange={e=>setProfile({...profile, occupation: e.target.value})} className="rounded-xl" data-testid="profile-occupation" />
              <Textarea placeholder="About me" rows={3} value={profile.about || ""} onChange={e=>setProfile({...profile, about: e.target.value})} className="rounded-xl md:col-span-2" data-testid="profile-about" />
              <Input placeholder="Interests (comma-separated)" value={profile.interests || ""} onChange={e=>setProfile({...profile, interests: e.target.value})} className="rounded-xl md:col-span-2" data-testid="profile-interests" />
              <Input placeholder="Relationship goals" value={profile.relationship_goals || ""} onChange={e=>setProfile({...profile, relationship_goals: e.target.value})} className="rounded-xl" data-testid="profile-goals" />
              <Input placeholder="Pets" value={profile.pets || ""} onChange={e=>setProfile({...profile, pets: e.target.value})} className="rounded-xl" data-testid="profile-pets" />
              <Input placeholder="Children" value={profile.children || ""} onChange={e=>setProfile({...profile, children: e.target.value})} className="rounded-xl" data-testid="profile-children" />
              <Input placeholder="Favorite date idea" value={profile.favorite_date_idea || ""} onChange={e=>setProfile({...profile, favorite_date_idea: e.target.value})} className="rounded-xl" data-testid="profile-date-idea" />
              <Button onClick={saveProfile} className="rounded-full bg-black text-white md:col-span-2 btn-pill" data-testid="profile-save">Save profile</Button>
            </div>
          </div>
        </TabsContent>

        {/* Questionnaire */}
        <TabsContent value="questionnaire">
          <div className="card-elegant p-8 text-center">
            <div className="font-heading text-2xl">{user?.questionnaire_completed ? "Your questionnaire is complete." : "Complete your compatibility questionnaire"}</div>
            <p className="text-neutral-600 mt-3 leading-relaxed max-w-xl mx-auto">30 thoughtful questions across lifestyle, values, personality, and preferences — helps you and future matches see your compatibility score.</p>
            <Link to="/questionnaire" className="inline-block mt-6"><Button className="rounded-full bg-black text-white btn-pill" data-testid="go-questionnaire">{user?.questionnaire_completed ? "Review answers" : "Start questionnaire"}</Button></Link>
          </div>
        </TabsContent>

        {/* VIP */}
        <TabsContent value="vip">
          {isVip ? (
            <div className="card-elegant p-8">
              <div className="flex items-center gap-3 mb-4">
                <Crown size={22} className="text-amber-500" />
                <h2 className="font-heading text-3xl">You're a VIP Member</h2>
              </div>
              <div className="grid sm:grid-cols-2 gap-4 text-sm text-neutral-700">
                <div><span className="text-neutral-500">Plan:</span> {plans[subscription.plan]?.name || subscription.plan}</div>
                <div><span className="text-neutral-500">Status:</span> {subscription.status}{subscription.cancel_at_period_end ? " (ends at period end)" : ""}</div>
              </div>
              {!subscription.cancel_at_period_end && (
                <Button variant="outline" onClick={cancelVip} className="mt-6 rounded-full" data-testid="cancel-vip-btn">Cancel VIP</Button>
              )}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-6">
              {Object.entries(plans).map(([key, plan]) => (
                <div key={key} className="card-elegant p-8 relative" data-testid={`vip-plan-${key}`}>
                  {key === "vip_annual" && <Badge className="absolute -top-3 right-6 bg-amber-500 text-black rounded-full shadow z-10">Best value</Badge>}
                  <div className="text-eyebrow gold">{plan.name}</div>
                  <div className="mt-3 flex items-baseline gap-1">
                    <span className="font-heading text-5xl">${(plan.amount_cents / 100).toFixed(0)}</span>
                    <span className="text-neutral-500 text-sm">/ {plan.interval}</span>
                  </div>
                  <ul className="mt-6 space-y-2 text-sm text-neutral-700">
                    <li>✦ 24-hour early access to every new event</li>
                    <li>✦ Compatibility scores unlocked on every attendee</li>
                    <li>✦ Concierge for private and custom evenings</li>
                    <li>✦ Priority waitlist bumps</li>
                  </ul>
                  <Button onClick={()=>startCheckout(key)} disabled={vipBusy === key} className="mt-8 w-full rounded-full bg-black text-white btn-pill disabled:opacity-70" data-testid={`subscribe-${key}`}>
                    {vipBusy === key ? "Redirecting…" : "Become VIP"}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

const Empty = ({ text, cta }) => (
  <div className="card-elegant p-12 text-center">
    <div className="text-neutral-600">{text}</div>
    {cta && <Link to="/events" className="inline-block mt-6"><Button className="rounded-full bg-black text-white btn-pill">Browse events</Button></Link>}
  </div>
);

const TicketCard = ({ t }) => (
  <div className="card-elegant p-6" data-testid={`ticket-${t.ticket_id}`}>
    <div className="text-eyebrow">{t.status === "paid" ? "Confirmed" : t.status}</div>
    <h3 className="font-heading text-2xl mt-2">{t.event?.title}</h3>
    <div className="text-neutral-600 text-sm mt-2">{t.event?.date && formatEventDate(t.event.date, { weekday: "short", month: "long", day: "numeric" })} · {t.event?.time}</div>
    <div className="text-neutral-600 text-sm">{t.event?.venue}</div>
    <div className="mt-4 flex items-baseline justify-between">
      <span className="font-mono text-lg gold tracking-widest">{t.ticket_code}</span>
      <span className="text-xs text-neutral-500">Qty: {t.quantity}</span>
    </div>
    {t.status === "paid" && t.ticket_code && (
      <Link
        to={`/tickets/${t.ticket_code}`}
        className="mt-4 inline-flex items-center justify-center w-full rounded-full border border-neutral-200 hover:border-black text-sm py-2 transition"
        data-testid={`view-my-ticket-${t.ticket_id}`}
      >
        Show QR at the door →
      </Link>
    )}
  </div>
);
